<script>
  import { onMount } from 'svelte';
  import { createEventDispatcher } from 'svelte';
  import { format } from 'svelte-i18n';
  import { Button, CardBody, CardHeader, Fade, Input, Spinner } from '@sveltestrap/sveltestrap';

  import { Icon } from '@client/components/icon';

  import { editBizcard, isEnabled } from '@client/services/ai/bizcards';

  const dispatch = createEventDispatcher();

  export let target = undefined;
  export let template;

  let abortController = null;
  let before;
  let enabled = false;
  let error = false;
  let errorMessage = '';
  let loading = false;
  let pendingApproval = false;
  let prompt = '';
  let rows = 3;

  $: if (prompt) {
    const lineCount = prompt.split('\n').length;
    rows = Math.min(Math.max(3, lineCount), 6);
  } else {
    rows = 3;
  }

  const handleClose = () => {
    // Cancel any pending request
    if (abortController) {
      abortController.abort();
      abortController = null;
    }

    prompt = '';
    loading = false;
    dispatch('close');
    target = undefined;
  };

  const handleSend = async () => {
    if (prompt.trim()) {
      loading = true;
      before = template;

      // Create new abort controller for this request
      abortController = new AbortController();

      const resp = await editBizcard({
        element: target,
        prompt,
        template,
        signal: abortController.signal,
        onStreamStart: () => {
          console.log('Loading response');
        }
      });

      // Clear abort controller after request completes
      abortController = null;

      if (resp.template) {
        errorMessage = '';
        pendingApproval = true;
        prompt = '';
        dispatch('edit', resp.template);
      } else if (resp.error) {
        error = true;
        errorMessage = resp.error;
        console.error(resp.error);
      }

      loading = false;
    }
  };

  const handleKeydown = (e) => {
    error = false;
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleAccept = (e) => {
    before = undefined;
    pendingApproval = false;
    dispatch('close');
    target = undefined;
  };

  const handleReject = (e) => {
    dispatch('edit', before);
    pendingApproval = false;
  };

  onMount(async () => {
    enabled = await isEnabled();
  });
</script>

<Fade isOpen={enabled && !!target}>
  <div class="wrapper">
    <div class="card border-0">
      <CardHeader class="pb-2 d-flex align-items-center">
        <Icon name="cpu" style="transform: rotate(10.1deg)" class="d-inline-block me-1" />
        {@html $format('label.DESIGN_ASSISTANT')}
        <Button close class="ms-auto" on:click={handleClose} />
      </CardHeader>
      <CardBody>
        {#if pendingApproval}
          <div class="buttons">
            <Button color="primary" size="sm" on:click={handleAccept}>
              <Icon name="check" /> Accept
            </Button>
            <Button size="sm" on:click={handleReject}>
              <Icon name="rotate-ccw" /> Undo
            </Button>
          </div>
        {:else}
          {#if error}
            <small class="text-danger">{errorMessage}</small>
          {/if}
          <div class="input-wrapper">
            <Input
              type="textarea"
              disabled={loading}
              invalid={error}
              placeholder={$format('label.PLACEHOLDER.AI_DESIGN_ASSIST')}
              bind:value={prompt}
              on:keydown={handleKeydown}
              {rows}
            />

            <Button color="link" class="p-0" disabled={loading} on:click={handleSend}>
              {#if loading}
                <Spinner color="primary" size="sm" />
              {:else}
                <Icon name="send" />
              {/if}
            </Button>
          </div>
        {/if}
      </CardBody>
    </div>
  </div>
</Fade>

<style lang="scss" src="./ai.scss"></style>
